/**
 * User Management Component
 *
 * Manages tenant users (end users within organizations)
 * Separate from admin portal users
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Block as InactiveIcon,
  Group as GroupIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PersonAdd as PersonAddIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import api, { userAPI, tenantAPI, groupAPI, User, UserRole, Group } from '../services/api';

interface Tenant {
  id: string;
  tenant_id: string;
  organization_name: string;
}

// Roles matching ADMIN_PORTAL_FEATURE_FLAGS_SCHEMA.md
const ROLES = [
  { value: 'admin', label: 'Administrator', description: 'Full access to all features', color: 'error' as const },
  { value: 'analyst', label: 'Analyst', description: 'Can view and analyze data, create reports', color: 'info' as const },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to dashboards and reports', color: 'success' as const },
  { value: 'editor', label: 'Editor', description: 'Can upload data and create maps', color: 'warning' as const },
];

const UserManagement: React.FC = () => {
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Group assignment state
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [selectedUserForGroup, setSelectedUserForGroup] = useState<User | null>(null);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);

  // Firebase sync state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [selectedUserForSync, setSelectedUserForSync] = useState<User | null>(null);
  const [syncPassword, setSyncPassword] = useState('');
  const [showSyncPassword, setShowSyncPassword] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    tenant_id: '',
    email: '',
    full_name: '',
    username: '',
    role: 'viewer',
    department: '',
    title: '',
    phone: '',
  });

  // Load users and tenants
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await userAPI.list({
        search: searchQuery.trim() || undefined,
        role: (roleFilter as UserRole) || undefined,
        is_active: statusFilter ? statusFilter === 'active' : undefined,
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      });
      setUsers(data.users || []);
      setTotalUsers(data.total ?? data.users?.length ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const tenants = await tenantAPI.list();
      setTenants(tenants || []);
    } catch (err: any) {
      console.error('Failed to load tenants:', err);
      setError(
        err.response?.data?.detail ||
        'Failed to load organizations. Please refresh the page.'
      );
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  // Reload from the server whenever filters or the page change. Search is
  // debounced so we don't fire a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      loadUsers();
    }, searchQuery ? 300 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, roleFilter, statusFilter, page, rowsPerPage]);

  // Filter changes jump back to the first page so matching results aren't
  // hidden behind a stale offset.
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    setPage(0);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      tenant_id: '',
      email: '',
      full_name: '',
      username: '',
      role: 'viewer',
      department: '',
      title: '',
      phone: '',
    });
    setEditingUser(null);
  };

  // Open invite dialog
  const handleOpenInvite = () => {
    resetForm();
    setInviteDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      tenant_id: user.tenant_id,
      email: user.email,
      full_name: user.full_name || '',
      username: user.username || '',
      role: user.role,
      department: user.department || '',
      title: user.title || '',
      phone: user.phone || '',
    });
    setEditDialogOpen(true);
  };

  // Handle user creation
  const handleCreateUser = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await userAPI.create({
        tenant_id: formData.tenant_id,
        email: formData.email.trim(),
        full_name: formData.full_name.trim() || undefined,
        username: formData.username.trim() || undefined,
        role: formData.role as UserRole,
        department: formData.department.trim() || undefined,
        title: formData.title.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        send_invite_email: false, // Invitations are sent when user is added to a project
      });

      setSuccess('User created successfully. They will receive an invitation email when added to a project.');
      setInviteDialogOpen(false);
      resetForm();
      try {
        await loadUsers();
      } catch (reloadErr) {
        console.error('Failed to reload users:', reloadErr);
        setError('User created, but list failed to refresh. Click Refresh to see updates.');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  // Handle user update
  const handleUpdateUser = async () => {
    if (!editingUser) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await userAPI.update(editingUser.id, {
        full_name: formData.full_name.trim() || undefined,
        role: formData.role as UserRole,
        department: formData.department.trim() || undefined,
        title: formData.title.trim() || undefined,
        phone: formData.phone.trim() || undefined,
      });

      setSuccess('User updated successfully');
      setEditDialogOpen(false);
      resetForm();
      try {
        await loadUsers();
      } catch (reloadErr) {
        console.error('Failed to reload users:', reloadErr);
        setError('User updated, but list failed to refresh. Click Refresh to see updates.');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await userAPI.delete(userId);
      setSuccess('User deleted successfully');
      try {
        await loadUsers();
      } catch (reloadErr) {
        console.error('Failed to reload users:', reloadErr);
        setError('User deleted, but list failed to refresh. Click Refresh to see updates.');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle user status
  const handleToggleStatus = async (user: User) => {
    setLoading(true);
    setError(null);
    try {
      await userAPI.toggleStatus(user.id);
      setSuccess(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      try {
        await loadUsers();
      } catch (reloadErr) {
        console.error('Failed to reload users:', reloadErr);
        setError('Status changed, but list failed to refresh. Click Refresh to see updates.');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle user status');
    } finally {
      setLoading(false);
    }
  };

  // Get role color from ROLES constant
  const getRoleColor = (role: string): 'error' | 'info' | 'success' | 'warning' | 'default' => {
    const roleConfig = ROLES.find((r) => r.value === role);
    return roleConfig?.color || 'default';
  };

  // Group management functions
  const handleOpenGroupDialog = async (user: User) => {
    setSelectedUserForGroup(user);
    setGroupDialogOpen(true);
    setGroupLoading(true);

    try {
      // Load all groups for the user's tenant
      const groupsResponse = await groupAPI.list(user.tenant_id);
      setAllGroups(groupsResponse.groups || []);

      // Find which groups this user belongs to by checking each group's members
      const userGroupIds: string[] = [];
      const failedGroups: string[] = [];
      for (const group of groupsResponse.groups || []) {
        try {
          const membersResponse = await groupAPI.getMembers(group.id);
          const isMember = (membersResponse.members || []).some(
            (m: any) => m.id === user.id
          );
          if (isMember) {
            userGroupIds.push(group.id);
          }
        } catch (e: any) {
          console.error(`Failed to fetch members for group ${group.id}:`, e);
          failedGroups.push(group.name);
        }
      }
      setUserGroups(userGroupIds);

      if (failedGroups.length > 0) {
        setError(
          `Could not verify membership for: ${failedGroups.join(', ')}. Status may be inaccurate.`
        );
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load groups');
    } finally {
      setGroupLoading(false);
    }
  };

  const handleGroupToggle = async (groupId: string, isCurrentlyMember: boolean) => {
    if (!selectedUserForGroup) return;

    setGroupLoading(true);
    try {
      if (isCurrentlyMember) {
        // Remove from group
        await groupAPI.removeMember(groupId, selectedUserForGroup.id);
        setUserGroups((prev) => prev.filter((id) => id !== groupId));
        setSuccess('User removed from group');
      } else {
        // Add to group
        await groupAPI.addMember({
          user_id: selectedUserForGroup.id,
          group_id: groupId,
        });
        setUserGroups((prev) => [...prev, groupId]);
        setSuccess('User added to group');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update group membership');
    } finally {
      setGroupLoading(false);
    }
  };

  const handleCloseGroupDialog = () => {
    setGroupDialogOpen(false);
    setSelectedUserForGroup(null);
    setAllGroups([]);
    setUserGroups([]);
  };

  // Firebase sync functions
  const handleOpenSyncDialog = (user: User) => {
    setSelectedUserForSync(user);
    setSyncPassword('');
    setShowSyncPassword(false);
    setSyncDialogOpen(true);
  };

  const handleCloseSyncDialog = () => {
    setSyncDialogOpen(false);
    setSelectedUserForSync(null);
    setSyncPassword('');
    setShowSyncPassword(false);
  };

  const handleSyncToFirebase = async () => {
    if (!selectedUserForSync) return;

    setSyncLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post(`/users/${selectedUserForSync.id}/sync-firebase`, {
        password: syncPassword || undefined,
      });

      const data = response.data;

      if (data.firebase_status === 'already_exists') {
        setSuccess(`${selectedUserForSync.email} already has a Firebase account. ${data.password_reset_sent ? 'Password reset email sent.' : 'They can use their existing password.'}`);
      } else if (data.password_set) {
        setSuccess(`Firebase account created for ${selectedUserForSync.email}. They can login immediately with the password you set.`);
      } else {
        setSuccess(`Firebase account created for ${selectedUserForSync.email}. Password reset email sent.`);
      }

      handleCloseSyncDialog();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to sync user to Firebase');
    } finally {
      setSyncLoading(false);
    }
  };

  // Validate form
  const isFormValid = () => {
    return (
      formData.tenant_id !== '' &&
      formData.email.trim() !== '' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    );
  };

  return (
    <Box>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Create users here, then add them to projects via Access Control. Invitations are sent when added to first project.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadUsers}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleOpenInvite}>
            Create User
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => handleRoleFilterChange(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                {ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => handleStatusFilterChange(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    {searchQuery || roleFilter || statusFilter
                      ? 'No users found matching the filters'
                      : 'No users found. Invite your first user!'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {user.full_name || user.username || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {tenants.find(t => t.id === user.tenant_id)?.organization_name || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={user.role} color={getRoleColor(user.role)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={user.is_active ? <ActiveIcon /> : <InactiveIcon />}
                      label={user.is_active ? 'Active' : 'Inactive'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Sync to Firebase (enable login)">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleOpenSyncDialog(user)}
                      >
                        <SyncIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage Groups">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleOpenGroupDialog(user)}
                      >
                        <GroupIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEdit(user)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.is_active ? 'Deactivate' : 'Activate'}>
                      <IconButton
                        size="small"
                        color={user.is_active ? 'warning' : 'success'}
                        onClick={() => handleToggleStatus(user)}
                      >
                        {user.is_active ? <InactiveIcon /> : <ActiveIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          handleDeleteUser(
                            user.id,
                            user.full_name || user.email
                          )
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      {/* Create User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => {
          setInviteDialogOpen(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>How invitations work:</strong> Create the user here, then add them to a project via Project Access Control.
              They will receive an invitation email with login instructions when added to their first project.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={formData.tenant_id}
                    onChange={(e) => handleInputChange('tenant_id', e.target.value)}
                    label="Organization"
                  >
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.organization_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  helperText="User will login with this email address"
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    label="Role"
                  >
                    {ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Department"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setInviteDialogOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleCreateUser}
            disabled={loading || !isFormValid()}
          >
            {loading ? <CircularProgress size={24} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Email cannot be changed. Contact support if email change is needed.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization"
                  value={tenants.find(t => t.id === formData.tenant_id)?.organization_name || 'Unknown'}
                  disabled
                  helperText="Organization cannot be changed after user creation"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  disabled
                  helperText="Email is read-only"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    label="Role"
                  >
                    {ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Department"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateUser}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Assignment Dialog */}
      <Dialog
        open={groupDialogOpen}
        onClose={handleCloseGroupDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Groups for {selectedUserForGroup?.full_name || selectedUserForGroup?.email}
        </DialogTitle>
        <DialogContent>
          {groupLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : allGroups.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No groups found for this organization. Create groups in the Groups section first.
            </Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click on a group to add or remove the user from it.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {allGroups.map((group) => {
                  const isMember = userGroups.includes(group.id);
                  return (
                    <Paper
                      key={group.id}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: isMember ? '2px solid' : '1px solid',
                        borderColor: isMember ? 'primary.main' : 'divider',
                        backgroundColor: isMember ? 'action.selected' : 'background.paper',
                        '&:hover': {
                          backgroundColor: isMember ? 'action.selected' : 'action.hover',
                        },
                      }}
                      onClick={() => handleGroupToggle(group.id, isMember)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {group.name}
                          </Typography>
                          {group.description && (
                            <Typography variant="body2" color="text.secondary">
                              {group.description}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={isMember ? 'Member' : 'Not Member'}
                          color={isMember ? 'primary' : 'default'}
                          size="small"
                        />
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGroupDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sync to Firebase Dialog */}
      <Dialog
        open={syncDialogOpen}
        onClose={handleCloseSyncDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Sync User to Firebase
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              This will create a Firebase account for <strong>{selectedUserForSync?.email}</strong>,
              allowing them to login to GUS with email/password authentication.
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can either:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 3 }}>
              <li>
                <Typography variant="body2">
                  <strong>Set a password</strong> - User can login immediately
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Leave blank</strong> - User will receive a password reset email
                </Typography>
              </li>
            </Box>

            <TextField
              fullWidth
              label="Password (optional)"
              type={showSyncPassword ? 'text' : 'password'}
              value={syncPassword}
              onChange={(e) => setSyncPassword(e.target.value)}
              placeholder="Leave blank to send password reset email"
              helperText={syncPassword.length > 0 && syncPassword.length < 8
                ? "Password must be at least 8 characters"
                : "Minimum 8 characters if setting a password"}
              error={syncPassword.length > 0 && syncPassword.length < 8}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowSyncPassword(!showSyncPassword)}
                      edge="end"
                    >
                      {showSyncPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSyncDialog} disabled={syncLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSyncToFirebase}
            variant="contained"
            color="primary"
            disabled={syncLoading || (syncPassword.length > 0 && syncPassword.length < 8)}
            startIcon={syncLoading ? <CircularProgress size={20} /> : <SyncIcon />}
          >
            {syncLoading ? 'Syncing...' : 'Sync to Firebase'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
